import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import PreviewFrame from './PreviewFrame';
import ProjectMediaManager from './ProjectMediaManager';
import AndroidPackagePanel from './AndroidPackagePanel';
import { useLocale } from '../i18n/LocaleContext';

const STATUS_KEYS = new Set(['planning', 'processing', 'deploying', 'preview_ready', 'built', 'live', 'attention_required', 'failed']);

function projectUrls(project) {
  return {
    preview: project.urls?.preview || project.previewUrl,
    repository: project.urls?.repository || project.repoUrl,
    live: project.urls?.live || project.deployUrl,
    deploymentDashboard: project.urls?.deploymentDashboard || project.deployDashboardUrl
  };
}

function projectPreviewPages(project) { return Array.isArray(project?.previewPages) ? project.previewPages : []; }

function ProjectCenter({ onNavigateToBuilder }) {
  const { t, dateLocale } = useLocale();
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [environment, setEnvironment] = useState(null);
  const [environmentName, setEnvironmentName] = useState('');
  const [environmentValue, setEnvironmentValue] = useState('');
  const [environmentSaving, setEnvironmentSaving] = useState(false);
  const [healthChecking, setHealthChecking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRefreshing, setPreviewRefreshing] = useState(false);
  const [previewPages, setPreviewPages] = useState([]);

  const refresh = () => {
    setLoading(true);
    setError('');
    api.projects()
      .then(res => {
        setProjects(res.projects || []);
        setSelectedId(current => current && (res.projects || []).some(project => project.id === current) ? current : res.projects?.[0]?.id || null);
      })
      .catch(err => setError(err.response?.data?.error || t('projects.loadError')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    if (!selectedId) { setEnvironment(null); return; }
    setPreviewOpen(false);
    setEnvironment(null);
    api.projectEnvironment(selectedId).then(res => setEnvironment(res.environment)).catch(() => setEnvironment({ configuredNames: [], unavailable: true }));
  }, [selectedId]);

  const selected = useMemo(() => projects.find(project => project.id === selectedId) || null, [projects, selectedId]);
  const selectedUrls = selected ? projectUrls(selected) : {};

  const renewSelectedPreview = async () => {
    if (!selected) return null;
    setPreviewRefreshing(true);
    try {
      const renewed = await api.refreshProjectPreview(selected.id);
      if (renewed.previewUrl) {
        const nextPages = renewed.previewPages || projectPreviewPages(selected);
        setProjects(current => current.map(project => project.id === selected.id ? { ...project, previewUrl: renewed.previewUrl, previewPages: nextPages, previewExpiresAt: renewed.expiresAt || project.previewExpiresAt || null, urls: { ...(project.urls || {}), preview: renewed.previewUrl } } : project));
        setPreviewPages(nextPages);
        return { previewUrl: renewed.previewUrl, previewPages: nextPages, expiresAt: renewed.expiresAt || null };
      }
      if (!renewed.jobId) throw new Error(t('projects.preparePreview'));
      for (let attempt = 0; attempt < 75; attempt++) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const status = await api.executionJob(renewed.jobId);
        if (status.job?.state === 'FAILED' || status.job?.state === 'CANCELLED') throw new Error(t('projects.pendingVerification'));
        if (status.job?.state !== 'SUCCEEDED') continue;
        const latest = await api.project(selected.id);
        const latestProject = latest.project;
        const jobId = latestProject?.sandboxExecution?.jobId;
        if (!jobId) throw new Error(t('projects.preparePreview'));
        const session = await api.createSandboxPreviewSession(jobId);
        const nextPages = session.previewPages || latestProject.previewPages || [];
        setProjects(current => current.map(project => project.id === selected.id ? { ...latestProject, previewUrl: session.previewUrl, previewPages: nextPages, previewExpiresAt: session.expiresAt || latestProject.previewExpiresAt || null, urls: { ...(latestProject.urls || {}), preview: session.previewUrl } } : project));
        setPreviewPages(nextPages);
        return { previewUrl: session.previewUrl, previewPages: nextPages, expiresAt: session.expiresAt || null };
      }
      throw new Error(t('projects.preparingPreview'));
    } finally { setPreviewRefreshing(false); }
  };

  const openPreview = async () => {
    setError('');
    try {
      const renewed = await renewSelectedPreview();
      setPreviewPages(renewed?.previewPages || projectPreviewPages(selected));
      setPreviewOpen(true);
    } catch (err) { setError(err.response?.data?.error || t('projects.preparePreview')); }
  };

  const handleMediaVerified = ({ previewUrl, previewPages: nextPages, expiresAt, jobId }) => {
    if (!selected) return;
    setPreviewPages(nextPages || []);
    setProjects(current => current.map(project => project.id === selected.id ? {
      ...project,
      status: 'preview_ready',
      taskStatus: 'completed',
      currentStage: 'Verified media update',
      previewUrl,
      previewPages: nextPages || [],
      previewExpiresAt: expiresAt || null,
      urls: { ...(project.urls || {}), preview: previewUrl },
      sandboxExecution: { ...(project.sandboxExecution || {}), jobId, state: 'SUCCEEDED' }
    } : project));
    setPreviewOpen(true);
  };

  const saveEnvironmentValue = async (event) => {
    event.preventDefault();
    if (!selected || !environmentName.trim() || !environmentValue) return;
    setEnvironmentSaving(true); setError('');
    try {
      const result = await api.saveProjectEnvironment(selected.id, { [environmentName.trim()]: environmentValue });
      setEnvironment(result.environment); setEnvironmentName(''); setEnvironmentValue('');
    } catch (err) { setError(err.response?.data?.error || t('projects.saveValue')); }
    finally { setEnvironmentSaving(false); }
  };

  const checkLiveHealth = async () => {
    if (!selected) return;
    setHealthChecking(true); setError('');
    try {
      const result = await api.checkProjectHealth(selected.id);
      setProjects(current => current.map(project => project.id === selected.id ? result.project : project));
    } catch (err) { setError(err.response?.data?.error || t('projects.checkLive')); }
    finally { setHealthChecking(false); }
  };

  const deleteSelectedProject = async () => {
    if (!selected) return;
    if (!window.confirm(t('projects.deleteConfirmation', { name: selected.name }))) return;
    setDeleting(true); setError('');
    try {
      await api.deleteProject(selected.id);
      setProjects(current => current.filter(project => project.id !== selected.id));
      setSelectedId(null);
    } catch (err) { setError(err.response?.data?.error || t('projects.deleteProject')); }
    finally { setDeleting(false); }
  };

  return (
    <div className="project-center">
      <div className="project-center-header">
        <div><div className="section-heading">{t('projects.title')}</div><p className="subtitle">{t('projects.description')}</p></div>
        <div className="project-center-actions">
          <button type="button" className="btn project-refresh-btn" onClick={refresh} disabled={loading}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={loading ? 'spin' : ''}><path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /></svg>{loading ? t('common.loading') : t('common.refresh')}</button>
          <button type="button" className="submit-btn project-build-btn" onClick={onNavigateToBuilder}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>{t('projects.buildProject')}</button>
        </div>
      </div>

      {error && <div className="auth-error">{error}</div>}
      {loading && <div className="no-logs">{t('projects.loading')}</div>}
      {!loading && projects.length === 0 && <div className="project-empty-state"><strong>{t('projects.emptyTitle')}</strong><span>{t('projects.emptyDescription')}</span><button type="button" className="submit-btn" onClick={onNavigateToBuilder}>{t('projects.createFirst')}</button></div>}

      {!loading && projects.length > 0 && (
        <div className="project-center-layout">
          <div className="project-list" aria-label={t('deploy.title')}>
            {projects.map(project => {
              const urls = projectUrls(project); const isSelected = project.id === selectedId;
              return <button type="button" key={project.id} className={`project-card ${isSelected ? 'selected' : ''}`} onClick={() => setSelectedId(project.id)}>
                <div className="project-card-topline"><strong>{project.name || t('projects.untitled')}</strong><span className={`project-status ${project.status || project.taskStatus}`}>{STATUS_KEYS.has(project.status) ? t(`projects.status.${project.status}`) : project.status || project.taskStatus}</span></div>
                <span className="project-card-meta">{project.type || t('projects.project')} · {project.currentStage || t('projects.queued')}</span>
                <div className="project-card-signals">{project.quality?.status === 'passed' && <span>{t('projects.qualityChecked')}</span>}{urls.preview && <span>{t('projects.preview')}</span>}{urls.live && <span>{t('projects.live')}</span>}</div>
              </button>;
            })}
          </div>

          {selected && <section className="project-detail" aria-live="polite">
            <div className="project-detail-title">
              <div><div className="eyebrow">{t('projects.workspace')}</div><h2>{selected.name}</h2><p>{selected.type || t('projects.project')} · {t('projects.updated', { date: new Date(selected.updatedAt || selected.createdAt).toLocaleString(dateLocale) })}</p></div>
              <div className="project-detail-title-actions"><span className={`project-status ${selected.status}`}>{STATUS_KEYS.has(selected.status) ? t(`projects.status.${selected.status}`) : selected.status}</span><button type="button" className="btn project-delete-btn" onClick={deleteSelectedProject} disabled={deleting}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14ZM10 11v6M14 11v6" /></svg>{deleting ? t('projects.deleting') : t('projects.deleteProject')}</button></div>
            </div>

            <div className="project-stage-panel"><div><span className="detail-label">{t('projects.currentStage')}</span><strong>{selected.currentStage || t('projects.queued')}</strong></div><div><span className="detail-label">{t('projects.qualityGate')}</span><strong>{selected.quality?.status === 'passed' ? `${selected.quality.score || 100}/100 · ${t('projects.passed')}` : t('projects.pendingVerification')}</strong></div><div><span className="detail-label">{t('projects.deployment')}</span><strong>{selected.deployment?.provider || t('common.notSelected')} · {selected.deployment?.status || t('common.notAvailable')}</strong></div></div>

            {selected.lastError && <div className="auth-error">{selected.lastError}</div>}
            <div className="project-link-row">
              <button type="button" className="btn project-action-primary" onClick={openPreview} disabled={previewRefreshing}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" /></svg>{previewRefreshing ? t('projects.preparingPreview') : selectedUrls.preview ? t('projects.openPreview') : t('projects.preparePreview')}</button>
              {selectedUrls.live && <a href={selectedUrls.live} target="_blank" rel="noreferrer">{t('projects.openLive')}</a>}
              {selectedUrls.live && <button type="button" className="btn" onClick={checkLiveHealth} disabled={healthChecking}>{healthChecking ? t('projects.checkingLive') : t('projects.checkLive')}</button>}
              {selectedUrls.repository && <a href={selectedUrls.repository} target="_blank" rel="noreferrer">{t('projects.openCode')}</a>}
              {selectedUrls.deploymentDashboard && <a href={selectedUrls.deploymentDashboard} target="_blank" rel="noreferrer">{t('projects.manageDeployment')}</a>}
              {!selectedUrls.live && selectedUrls.preview && <button type="button" className="btn project-action-secondary" onClick={onNavigateToBuilder}>{t('projects.reviewDeploy')}</button>}
            </div>

            {previewOpen && selectedUrls.preview && <div className="project-preview-panel"><PreviewFrame loading={false} previewUrl={selectedUrls.preview} pages={previewPages.length ? previewPages : projectPreviewPages(selected)} expiresAt={selected.previewExpiresAt} target={selected.target || 'WEB'} targetPlatforms={selected.targetPlatforms || []} title={`תצוגת הפרויקט: ${selected.name}`} returnLabel="חזרה לפרויקטים" onClose={() => setPreviewOpen(false)} onRefreshPreview={selected?.sandboxExecution?.jobId ? renewSelectedPreview : undefined} /></div>}

            <AndroidPackagePanel projectId={selected.id} target={selected.target} targetPlatforms={selected.targetPlatforms || []} />

            <ProjectMediaManager project={selected} onVerified={handleMediaVerified} />

            {selected.health && <div className={`project-health ${selected.health.status}`}><strong>Live status: {selected.health.status}</strong><span>{selected.health.detail} · {selected.health.responseTimeMs}ms · {new Date(selected.health.checkedAt).toLocaleString()}</span></div>}

            <div className="project-section"><div className="project-section-title">{t('projects.stages')}</div><div className="project-step-list">{(selected.steps || []).length === 0 && <span className="no-logs">{t('projects.stagesEmpty')}</span>}{(selected.steps || []).map(step => <div className={`project-step ${step.status}`} key={`${step.index}-${step.type}`}><span className="project-step-dot" /><span>{step.name}</span><small>{step.status}</small></div>)}</div></div>

            <div className="project-section"><div className="project-section-title">{t('projects.releases')}</div>{(selected.releases || []).length === 0 && <span className="no-logs">{t('projects.releasesEmpty')}</span>}{(selected.releases || []).map(release => <div className="release-row" key={release.id}><div><strong>Release {release.number}</strong><span>{release.note} · {new Date(release.createdAt).toLocaleString()}</span></div>{release.liveUrl && <a href={release.liveUrl} target="_blank" rel="noreferrer">{t('common.open')}</a>}</div>)}</div>

            <div className="project-section">
              <div className="project-section-title">{t('projects.productionEnvironment')}</div>
              <p className="project-section-note">{t('projects.environmentDescription')}</p>
              {environment === null && <span className="no-logs">{t('projects.environmentLoading')}</span>}
              {environment?.configuredNames?.length > 0 && <div className="environment-name-list">{environment.configuredNames.map(name => <span key={name}>{name}</span>)}</div>}
              {environment?.configuredNames?.length === 0 && <span className="no-logs">{t('projects.environmentEmpty')}</span>}
              <form className="environment-form" onSubmit={saveEnvironmentValue}><input value={environmentName} onChange={event => setEnvironmentName(event.target.value)} placeholder="VARIABLE_NAME" aria-label={t('projects.environmentName')} /><input type="password" value={environmentValue} onChange={event => setEnvironmentValue(event.target.value)} placeholder={t('projects.environmentValue')} aria-label={t('projects.environmentValue')} /><button type="submit" className="btn" disabled={environmentSaving || !environmentName.trim() || !environmentValue}>{environmentSaving ? t('projects.saving') : t('projects.saveValue')}</button></form>
            </div>
          </section>}
        </div>
      )}
    </div>
  );
}

export default ProjectCenter;
